import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { HttpClientModule, HttpClient } from "@angular/common/http";

import { RoutePaths } from "./configs/global";

/* COMPONENTS */
import { AppComponent } from "./app.component";
import { AppLayoutComponent } from "./components/layouts/layout.component";
import { LayoutPartialComponent } from "./components/layouts/layout-partial.component";

/* PAGES */
import { PageAuthIndexComponent } from "./components/pages/auth/index.component";
import { PageHomeIndexComponent } from "./components/pages/home/index.component";
import { PagePlanListComponent } from "./components/pages/plan/list/list.component";
import { PagePlanDetailComponent } from "./components/pages/plan/detail/detail.component";
import { PlanCalendarComponent } from "./components/pages/plan/calendar/calendar.component";
import { PageCartIndexComponent } from "./components/pages/cart/index/index.component";
import { PageCartAddComponent } from "./components/pages/cart/add/add.component";
import { StationMapComponent } from "./components/pages/plan/map/map.component";

import { PageReserveIndexComponent } from "./components/pages/reserve/index/index.component";
import { PageReserveCheckoutIndexComponent } from "./components/pages/reserve/checkout/index.component";
import { PageReserveFinishIndexComponent } from "./components/pages/reserve/finish/index.component";
import { PageReserveInsuranceIndexComponent } from "./components/pages/reserve/insurance/index.component";
import { PageReservePassengerPlan1Component } from "./components/pages/reserve/passenger/plan-1.component";
import { PageReservePassengerPlan2Component } from "./components/pages/reserve/passenger/plan-2.component";
import { PageReserveSeatMapPlan1Component } from "./components/pages/reserve/seat-map/plan-1.component";
import { PageReserveSeatMapPlan2Component } from "./components/pages/reserve/seat-map/plan-2.component";
import { PageReservePaymentIndexComponent } from "./components/pages/reserve/payment/index.component";
import { PlanListItemComponent } from "./components/pages/plan/list-item/list-item.component";

/* MY PAGE */
import { PageMyPageIndexComponent } from "./components/pages/mypage/index/index.component";
import { PageProfileIndexComponent } from "./components/pages/mypage/profile/index.component";
import { PageReservationListComponent } from "./components/pages/mypage/reservation-list/index.component";
import { PageReservationDetailComponent } from "./components/pages/mypage/reservation-detail/index.component";
import { PageReservationChangeComponent } from "./components/pages/mypage/reservation-change/index.component";
import { PageReservationConfirmComponent } from "./components/pages/mypage/reservation-confirm/index.component";
import { PageReservationEmailComponent } from "./components/pages/mypage/reservation-email/index.component";
import { PageReservationWebTicketComponent } from "./components/pages/mypage/reservation-webticket/index.component";
import { PageReceiptListComponent } from "./components/pages/mypage/receipt-list/index.component";
import { PageReservationEmailListComponent } from "./components/pages/mypage/reservation-email-list/index.component";

/* INFO PAGES */
import { PageInfoIndexComponent } from "./components/pages/info/index/index.component";
import { PageInfoMemberRuleIndexComponent } from "./components/pages/info/member-rule/index.component";
import { PageInfoSiteRuleIndexComponent } from "./components/pages/info/site-rule/index.component";
import { PageInfoTokusyoIndexComponent } from "./components/pages/info/tokusyo/index.component";
import { PageInfoPayIndexComponent } from "./components/pages/info/pay/index.component";
import { PageInfoCancelIndexComponent } from "./components/pages/info/cancel/index.component";
import { PageInfoCompanyIndexComponent } from "./components/pages/info/company/index.component";
import { PageInfoFaqsIndexComponent } from "./components/pages/info/faqs/index.component";
import { PageInfoPrivacyPolicyIndexComponent } from "./components/pages/info/privacy-policy/index.component";
import { PageInfoRefundIndexComponent } from "./components/pages/info/refund/index/index.component";
import { PageInfoRefundCheckoutComponent } from "./components/pages/info/refund/checkout/index.component";
import { PageInfoRefundFinishComponent } from "./components/pages/info/refund/finish/index.component";
import { PageInfoYakkanIndexComponent } from './components/pages/info/yakkan/index.component';
/* END */

/* AREA */
import { PageAreaCityDetailComponent } from "./components/pages/area/detail-city/index.component";
import { PageAreaTownDetailComponent } from "./components/pages/area/detail-town/index.component";
/* END */

import { SafeHTML } from "./pipes/safe-html.pipe";
/* END */

/* ROUTES */
const appRoutes: Routes = [
  /* HOME */
  {
    path: RoutePaths.pageHome,
    component: PageHomeIndexComponent,
  },

  /* AUTH */
  {
    path: RoutePaths.pageAuth,
    component: PageAuthIndexComponent,
  },

  /* PLANS */
  {
    path: RoutePaths.pagePlanList,
    component: PagePlanListComponent,
  },
  {
    path: RoutePaths.pagePlanListParam,
    component: PagePlanListComponent,
  },
  {
    path: RoutePaths.pagePlanListCityParam,
    component: PagePlanListComponent,
  },
  {
    path: RoutePaths.pagePlanListCityTownParam,
    component: PagePlanListComponent,
  },
  {
    path: RoutePaths.pagePlanListCityTownServiceParam,
    component: PagePlanListComponent,
  },
  {
    path: RoutePaths.pagePlanListCityTownServiceOfficeParam,
    component: PagePlanListComponent,
  },
  {
    path: RoutePaths.pagePlanDetail,
    component: PagePlanDetailComponent,
  },

  /* CART */
  {
    path: RoutePaths.pageCartIndex,
    component: PageCartIndexComponent,
  },
  {
    path: RoutePaths.pageCartAdd,
    component: PageCartAddComponent,
  },
  {
    path: RoutePaths.pageCartEdit,
    component: PageCartAddComponent,
  },

  /* MY PAGES */
  {
    path: RoutePaths.pageMyPageIndex,
    component: PageMyPageIndexComponent,
  },
  {
    path: RoutePaths.pageProfileIndex,
    component: PageProfileIndexComponent,
  },
  {
    path: RoutePaths.pageReservationList,
    component: PageReservationListComponent,
  },
  {
    path: RoutePaths.pageReservationListWithPage,
    component: PageReservationListComponent,
  },
  {
    path: RoutePaths.pageReservationDetail,
    component: PageReservationDetailComponent,
  },
  {
    path: RoutePaths.pageReservationChange,
    component: PageReservationChangeComponent,
  },
  {
    path: RoutePaths.pageReservationConfirm,
    component: PageReservationConfirmComponent,
  },
  {
    path: RoutePaths.pageReceiptList,
    component: PageReceiptListComponent,
  },
  {
    path: RoutePaths.pageReceiptListWithPage,
    component: PageReceiptListComponent,
  },
  {
    path: RoutePaths.pageReservationEmail,
    component: PageReservationEmailComponent,
  },
  {
    path: RoutePaths.pageReservationEmailWithPage,
    component: PageReservationEmailComponent,
  },
  {
    path: RoutePaths.pageEmailList,
    component: PageReservationEmailListComponent,
  },
  {
    path: RoutePaths.pageEmailListWithPage,
    component: PageReservationEmailListComponent,
  },
  {
    path: RoutePaths.pageReservationWebTicket,
    component: PageReservationWebTicketComponent,
  },

  /* RESERVES */
  {
    path: RoutePaths.pageReserveIndex,
    component: PageReserveIndexComponent,
  },
  {
    path: RoutePaths.pageReserveCheckout,
    component: PageReserveCheckoutIndexComponent,
  },
  {
    path: RoutePaths.pageReserveFinish,
    component: PageReserveFinishIndexComponent,
  },
  {
    path: RoutePaths.pageReserveInsurance,
    component: PageReserveInsuranceIndexComponent,
  },
  {
    path: RoutePaths.pageReservePassengerPlan1,
    component: PageReservePassengerPlan1Component,
  },
  {
    path: RoutePaths.pageReservePassengerPlan2,
    component: PageReservePassengerPlan2Component,
  },
  {
    path: RoutePaths.pageReserveSeatMapPlan1,
    component: PageReserveSeatMapPlan1Component,
  },
  {
    path: RoutePaths.pageReserveSeatMapPlan2,
    component: PageReserveSeatMapPlan2Component,
  },
  {
    path: RoutePaths.pageReservePayment,
    component: PageReservePaymentIndexComponent,
  },

  /* INFO PAGES */
  {
    path: RoutePaths.pageInfoIndex,
    component: PageInfoIndexComponent,
  },
  {
    path: RoutePaths.pageInfoMemberRuleIndex,
    component: PageInfoMemberRuleIndexComponent,
  },
  {
    path: RoutePaths.pageInfoSiteRuleIndex,
    component: PageInfoSiteRuleIndexComponent,
  },
  {
    path: RoutePaths.pageInfoTokusyoIndex,
    component: PageInfoTokusyoIndexComponent,
  },
  {
    path: RoutePaths.pageInfoPayIndex,
    component: PageInfoPayIndexComponent,
  },
  {
    path: RoutePaths.pageInfoFaqsIndex,
    component: PageInfoFaqsIndexComponent,
  },
  {
    path: RoutePaths.pageInfoCancelIndex,
    component: PageInfoCancelIndexComponent,
  },
  {
    path: RoutePaths.pageInfoCompanyIndex,
    component: PageInfoCompanyIndexComponent,
  },
  {
    path: RoutePaths.pageInfoPrivacyPolicyIndex,
    component: PageInfoPrivacyPolicyIndexComponent,
  },
  {
    path: RoutePaths.pageInfoRefundIndex,
    component: PageInfoRefundIndexComponent,
  },
  {
    path: RoutePaths.pageInfoRefundCheckout,
    component: PageInfoRefundCheckoutComponent,
  },
  {
    path: RoutePaths.pageInfoRefundFinish,
    component: PageInfoRefundFinishComponent,
  },
  {
    path: RoutePaths.pageInfoYakkanIndex,
    component: PageInfoYakkanIndexComponent
  },
  /* STATION MAP FORM EMAILBOX */
  {
    path: RoutePaths.pageStationDetail,
    component: PageHomeIndexComponent,
  },
  {
    path: RoutePaths.pageStationDetailD,
    component: PageHomeIndexComponent,
  },
  {
    path: RoutePaths.pageStationDetailWithPlanCode,
    component: PagePlanDetailComponent,
  },
  {
    path: RoutePaths.pageStationDetailWithPlanCodeD,
    component: PagePlanDetailComponent,
  },
  {
    path: RoutePaths.pageStationDetailWithDate,
    component: PagePlanDetailComponent,
  },
  {
    path: RoutePaths.pageStationDetailWithDateD,
    component: PagePlanDetailComponent,
  },
  /* END */
  /* AREA */
  {
    path: RoutePaths.pageAreaCityDetailIndex,
    component: PageAreaCityDetailComponent,
  },
  {
    path: RoutePaths.pageAreaTownDetailWithTownIndex,
    component: PageAreaTownDetailComponent,
  },
  /* END */
  /* END */

  /* NOT FOUND THEN GO TO BASE URL */
  {
    path: "**",
    redirectTo: "",
    pathMatch: "full",
  },
];
/* END */

@NgModule({
  declarations: [
    SafeHTML,
    AppComponent,
    AppLayoutComponent,
    LayoutPartialComponent,
    PageHomeIndexComponent,
    PagePlanListComponent,
    PagePlanDetailComponent,
    PlanCalendarComponent,
    PageCartIndexComponent,
    PageAuthIndexComponent,
    PageCartAddComponent,
    StationMapComponent,
    PageReserveIndexComponent,
    PageReserveCheckoutIndexComponent,
    PageReserveFinishIndexComponent,
    PageReserveInsuranceIndexComponent,
    PageReservePassengerPlan1Component,
    PageReservePassengerPlan2Component,
    PageReserveSeatMapPlan1Component,
    PageReserveSeatMapPlan2Component,
    PageReservePaymentIndexComponent,
    PageReservationEmailListComponent,
    PlanListItemComponent,
    PageMyPageIndexComponent,
    PageProfileIndexComponent,
    PageReservationListComponent,
    PageReservationDetailComponent,
    PageReservationChangeComponent,
    PageReservationConfirmComponent,
    PageReservationEmailComponent,
    PageReservationWebTicketComponent,
    PageReceiptListComponent,
    PageInfoIndexComponent,
    PageInfoMemberRuleIndexComponent,
    PageInfoSiteRuleIndexComponent,
    PageInfoTokusyoIndexComponent,
    PageInfoPayIndexComponent,
    PageInfoCancelIndexComponent,
    PageInfoCompanyIndexComponent,
    PageInfoFaqsIndexComponent,
    PageInfoPrivacyPolicyIndexComponent,
    PageInfoRefundIndexComponent,
    PageInfoRefundCheckoutComponent,
    PageInfoRefundFinishComponent,
    PageInfoYakkanIndexComponent,
    PageAreaCityDetailComponent,
    PageAreaTownDetailComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(appRoutes, {
      enableTracing: false /* debugging purposes only */,
      scrollPositionRestoration: "enabled" /* SCROLL TO TOP */,
    }),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [],
  bootstrap: [AppComponent],
})
export class AppModule {}

/* INIT FOR TRANSLATION */
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
/* END */
